/**
 * Fresh read of the fax questionnaire, bypassing any already-loaded snapshot.
 *
 * `loadFaxQuestionnaireDocument` short-circuits on the `questionnaire_details`
 * the data accumulator fetched once on mount, so it can never observe the
 * `in_progress` → `completed` transition written by background prepare. This
 * module re-queries Postgres on every call, which is what the status poller
 * needs. A PG miss (no `question_tree`) is "not ready yet" — keep polling;
 * do not fall back to Firestore, which can still hold last form's completed
 * tree after a re-select or before prepare's first write.
 *
 * @module procedureQuestionaire/utils/fetchFaxQuestionnaireDocument
 */
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { singleOrderDetailsGenericCallForPostgres } from "../../../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { hasQuestionnaireTree } from "./faxQuestionnairePersistence";

/** Minimal shape read from the submission table for questionnaire polling. */
interface SubmissionQuestionnaireResponse {
  submission?: { questionnaire_details?: unknown } | null;
}

/**
 * Re-reads the questionnaire from Postgres when `orgId` is present.
 *
 * Matches the fax PDF poller: an empty PG tree is a poll miss, not a cue to
 * read Firestore. Firestore is only used when PG cannot be queried (no org).
 *
 * @param orderId - Medical PA order id
 * @param orgId - Tenant org id; when absent only Firestore is read
 * @param signal - Optional AbortSignal for the Postgres request
 * @returns The questionnaire doc, or null when PG has no tree (or Firestore
 *   has none, if PG was skipped)
 */
export const fetchFaxQuestionnaireDocument = async (
  orderId: string,
  orgId: string | null | undefined,
  signal?: AbortSignal,
): Promise<Record<string, unknown> | null> => {
  if (orgId) {
    const response =
      await singleOrderDetailsGenericCallForPostgres<SubmissionQuestionnaireResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.SUBMISSION,
        },
        signal,
      );
    const pgDetails = response?.submission?.questionnaire_details;
    if (hasQuestionnaireTree(pgDetails)) {
      return pgDetails;
    }
    // PG miss = prepare has not written yet — keep polling, no Firestore.
    return null;
  }

  const firestoreDoc = await FirestoreService.getDocument<
    Record<string, unknown>
  >(`/medical_pa_orders/${orderId}/questionnaire/fax_questionnaire`);

  return hasQuestionnaireTree(firestoreDoc) ? firestoreDoc : null;
};
